import { useState, useEffect, useRef } from 'react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { Loader2 } from 'lucide-react'
import { Turnstile } from '@marsidev/react-turnstile'
import Modal from './Modal'

const DeleteAccountModal = ({ isOpen, onClose, onConfirm, isLoading, userEmail }) => {
  const [confirmEmail, setConfirmEmail] = useState('')
  const [confirmText, setConfirmText] = useState('')
  const [captchaToken, setCaptchaToken] = useState('')
  const [error, setError] = useState('')
  const turnstileRef = useRef()

  const CONFIRM_PHRASE = 'delete my account'

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setConfirmEmail('')
      setConfirmText('')
      setCaptchaToken('')
      setError('')
      // Reset Turnstile widget
      if (turnstileRef.current) {
        turnstileRef.current.reset()
      }
    }
  }, [isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    if (confirmEmail.trim().toLowerCase() !== (userEmail || '').trim().toLowerCase()) {
      setError('Email does not match your account email')
      return
    }

    if (confirmText.trim().toLowerCase() !== CONFIRM_PHRASE) {
      setError('Please type the confirmation phrase exactly as shown')
      return
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification')
      return
    }

    try {
      // Pass captchaToken to parent for API verification
      await onConfirm(captchaToken)
    } catch (error) {
      setError(error.message || 'Failed to delete account')
      // Reset captcha on error
      if (turnstileRef.current) {
        turnstileRef.current.reset()
        setCaptchaToken('')
      }
    }
  }

  const isEmailMatch = confirmEmail.trim().toLowerCase() === (userEmail || '').trim().toLowerCase()
  const isTextMatch = confirmText.trim().toLowerCase() === CONFIRM_PHRASE
  const isFormValid = isEmailMatch && isTextMatch && captchaToken

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Account"
      size="lg"
      preventClose={isLoading}
    >
      <div className="space-y-5">
        {/* Warning Banner */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-900">This will permanently delete your account</h4>
              <ul className="text-sm text-red-700 mt-2 list-disc list-inside space-y-1">
                <li>All your API keys will be revoked immediately</li>
                <li>You will no longer be billed, and after 30 days your account data will be permanently removed</li>
                <li>Your usage history and preferences will be deleted</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Confirmation Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email confirmation */}
          <div>
            <label htmlFor="confirmEmail" className="block text-sm text-gray-700 mb-2">
              Your email:
            </label>
            <input
              id="confirmEmail"
              type="email"
              value={confirmEmail}
              onChange={(e) => {
                setConfirmEmail(e.target.value)
                setError('')
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
              disabled={isLoading}
              autoComplete="off"
            />
          </div>

          {/* Delete my account confirmation */}
          <div>
            <label htmlFor="confirmText" className="block text-sm text-gray-700 mb-2">
              To verify, type{' '}
              <span 
                className="font-semibold text-red-600 italic select-none pointer-events-none"
                style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none', msUserSelect: 'none' }}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onDragStart={(e) => e.preventDefault()}
              >
                delete my account
              </span>
              {' '}exactly as it appears:
            </label>
            <input
              id="confirmText"
              type="text"
              value={confirmText}
              onChange={(e) => {
                setConfirmText(e.target.value)
                setError('')
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-colors"
              disabled={isLoading}
              autoComplete="off"
              spellCheck="false"
            />
          </div>

          {/* Cloudflare Turnstile CAPTCHA */}
          <div className="w-full py-1 pr-1">
            <div className="flex justify-center">
              <Turnstile
                ref={turnstileRef}
                siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                onSuccess={(token) => {
                  setCaptchaToken(token)
                  setError('')
                }}
                onError={() => {
                  setCaptchaToken('')
                  setError('CAPTCHA verification failed. Please try again.')
                }}
                onExpire={() => {
                  setCaptchaToken('')
                  setError('CAPTCHA expired. Please verify again.')
                }}
                options={{
                  theme: 'light',
                  size: 'flexible'
                }}
              />
            </div>
          </div>

          {error && (
            <p className="text-red-500 text-sm">{error}</p>
          )}

          <p className="text-sm text-gray-500">
            You'll be prompted to verify your identity before account deletion.
          </p>

          <button
            type="submit"
            disabled={isLoading || !isFormValid}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-red-600 bg-transparent hover:bg-red-600 hover:text-white border border-red-300 hover:border-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors duration-200"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Cancel plan and delete this account'
            )}
          </button>
        </form>
      </div>
    </Modal>
  )
}

export default DeleteAccountModal
